import React from 'react';
import {
  CheckCircle2,
  Clock,
  XCircle,
  FlaskConical,
  Send,
  FileCheck2,
  ShieldCheck,
  AlertTriangle,
} from 'lucide-react';
import { cn } from '@/shared/utils/utils';

export interface ConnectedLifecycleStageBarProps {
  documentType: 'quotation' | 'proforma' | 'sales_order' | 'sample' | 'direct_sale' | 'delivery_challan';
  documentStatus?: string;
  dealStage?: string;
  dealTitle?: string;
  lostReasonName?: string;
  lostReasonNote?: string;
  className?: string;
  // Accepted but not yet reflected in the step bar's own logic below — a
  // quotation/proforma reached via a sample trial still resolves its stage
  // purely from documentType/dealStage today. Kept as real props (not
  // dropped) so the call site's intent is visible for whoever wires this in.
  hasSample?: boolean;
  sampleCode?: string;
  // Same as above — accepted so the call site's intent (a way to trigger
  // marking this document's deal lost from the stage bar) is visible, not
  // yet rendered as an action here.
  onMarkLost?: () => void;
}

interface StepDefinition {
  key: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}

const LIFECYCLE_STEPS: StepDefinition[] = [
  { key: 'lead', label: 'Enquiry / Lead', icon: Clock },
  { key: 'sample', label: 'Sample / Trial', icon: FlaskConical },
  { key: 'quotation', label: 'Quotation', icon: Send },
  { key: 'proforma', label: 'Proforma / Neg.', icon: FileCheck2 },
  { key: 'won', label: 'Order / Won', icon: ShieldCheck },
];

function resolveActiveIndex(
  documentType: string,
  documentStatus?: string,
  dealStage?: string,
): number {
  if (dealStage === 'closed_lost' || documentStatus === 'cancelled') {
    return -1; // Lost state
  }

  // If deal stage gives detailed position
  if (dealStage) {
    const s = dealStage.toLowerCase();
    if (s.includes('won') || s === 'order_confirmed') return 4;
    if (s.includes('proforma') || s.includes('negotiat') || s.includes('trial_successful')) return 3;
    if (s.includes('quotation') || s.includes('proposal')) return 2;
    if (s.includes('sample') || s.includes('trial')) return 1;
    if (s.includes('lead') || s.includes('contacted') || s.includes('discovery')) return 0;
  }

  // Fallback to document type
  if (documentType === 'sales_order') return 4;
  if (documentType === 'proforma') return 3;
  if (documentType === 'quotation') return 2;
  if (documentType === 'sample') return 1;
  return 0;
}

export function ConnectedLifecycleStageBar({
  documentType,
  documentStatus,
  dealStage,
  dealTitle,
  lostReasonName,
  lostReasonNote,
  className,
}: ConnectedLifecycleStageBarProps) {
  const isLost = dealStage === 'closed_lost' || documentStatus === 'cancelled' || !!lostReasonName;
  const activeIndex = resolveActiveIndex(documentType, documentStatus, dealStage);

  return (
    <div
      className={cn(
        'rounded-2xl border bg-white p-4 shadow-xs transition-all',
        isLost ? 'border-rose-200 bg-rose-50/20' : 'border-slate-200/80',
        className,
      )}
    >
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 pb-3 mb-3">
        <div className="flex items-center gap-2">
          <span className="flex h-6 w-6 items-center justify-center rounded-lg bg-blue-50 text-blue-600 font-bold text-xs">
            ⇄
          </span>
          <span className="text-xs font-bold uppercase tracking-wider text-slate-700">
            Connected Sales Lifecycle
          </span>
          {dealTitle && (
            <>
              <span className="text-slate-300">•</span>
              <span className="text-xs text-slate-500 font-medium truncate max-w-[200px]" title={dealTitle}>
                {dealTitle}
              </span>
            </>
          )}
        </div>

        {isLost ? (
          <div className="flex items-center gap-1.5 rounded-lg bg-rose-100/80 px-2.5 py-1 text-xs font-bold text-rose-800">
            <XCircle className="h-3.5 w-3.5 text-rose-600" />
            <span>Closed Lost</span>
            {lostReasonName && <span className="font-normal opacity-90">— {lostReasonName}</span>}
          </div>
        ) : (
          <div className="flex items-center gap-1.5 text-xs text-slate-500 font-medium">
            <span className="inline-block h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
            <span>Active in Pipeline</span>
          </div>
        )}
      </div>

      {/* Stepper Bar */}
      <div className="relative flex items-center justify-between gap-2 overflow-x-auto py-1">
        {LIFECYCLE_STEPS.map((step, idx) => {
          const Icon = step.icon;
          const isCurrent = !isLost && idx === activeIndex;
          const isCompleted = !isLost && idx < activeIndex;
          const isPassedBeforeLost = isLost && idx <= (documentType === 'proforma' ? 3 : documentType === 'quotation' ? 2 : documentType === 'sample' ? 1 : 0);

          return (
            <React.Fragment key={step.key}>
              <div className="flex flex-col items-center gap-1.5 min-w-[72px] flex-1 text-center">
                <div
                  className={cn(
                    'flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold transition-all shadow-xs',
                    isCurrent
                      ? 'bg-blue-600 text-white ring-4 ring-blue-100'
                      : isCompleted
                      ? 'bg-emerald-600 text-white'
                      : isPassedBeforeLost
                      ? 'bg-slate-200 text-slate-600 line-through'
                      : 'bg-slate-100 text-slate-400',
                  )}
                >
                  {isCompleted ? (
                    <CheckCircle2 className="h-4 w-4" />
                  ) : (
                    <Icon className="h-3.5 w-3.5" />
                  )}
                </div>
                <span
                  className={cn(
                    'text-[11px] font-semibold leading-tight',
                    isCurrent
                      ? 'text-blue-700 font-bold'
                      : isCompleted
                      ? 'text-slate-800'
                      : 'text-slate-400',
                  )}
                >
                  {step.label}
                </span>
              </div>

              {idx < LIFECYCLE_STEPS.length - 1 && (
                <div
                  className={cn(
                    'h-0.5 flex-1 mb-5 transition-all',
                    !isLost && idx < activeIndex
                      ? 'bg-emerald-500'
                      : 'bg-slate-200',
                  )}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Lost Reason Detail Callout if lost */}
      {isLost && (
        <div className="mt-3 flex items-start gap-2.5 rounded-xl border border-rose-200/80 bg-rose-50/60 p-3 text-xs text-rose-900">
          <AlertTriangle className="h-4 w-4 text-rose-600 shrink-0 mt-0.5" />
          <div className="space-y-0.5">
            <p className="font-bold">
              Deal Outcome: Closed Lost
              {lostReasonName && <span className="font-medium"> ({lostReasonName})</span>}
            </p>
            {lostReasonNote && (
              <p className="text-rose-700 text-[11px]">Note: {lostReasonNote}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
